import { useColorMode, useColorModeValue } from '@chakra-ui/color-mode';
import { motion, isValidMotionProp, AnimatePresence } from 'framer-motion';
import {
    Box,
    Button,
    Flex,
    Portal,
    Tooltip,
    chakra,
    shouldForwardProp
} from '@chakra-ui/react';
import { CopyIcon, MotionBox, TrashDockIcon } from './UiElements';
import { useEffect, useRef, useState } from 'react';
import layoutStore from '../stores/layoutStore';
import { tooltipAnimation } from './customTheme';
import { ReminderPanel, ReminderPopover } from './ReminderPopover';
import { commentBg } from '../Utils/commentBg';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';
import { useCopyToClipboard } from '../Utils/useCopyToClipboard';
import useCustomToast from './ToastProvider';
import useStore from '../stores/store';
import { findGroupId, findItemInCurrentView } from '../Utils/findDataFunctions';

const btnStyles = {
    bg: 'transparent',
    h: '36px',
    w: '36px',
    p: '0',
    alignItems: 'center',
    size: 'sm',
    rounded: '11px'
};

const NoteToolbar = () => {
    /* const updateDockFromOutside = layoutStore((state) => state.updateDockFromOutside);
    useEffect(() => {}, [updateDockFromOutside]); */
    let noteId = layoutStore.getState().noteEditMode;
    let item = findItemInCurrentView(noteId);
    const isTodo = item?.todo ?? false;
    const handleMouseDown = (e) => {
        e.preventDefault();

        let noteIdToFocus = 'note-' + noteId;
        document.getElementById(noteIdToFocus)?.focus();
    };
    const deleteNote = () => {
        useStore.getState().deleteItems(noteId);
        layoutStore.getState().setNoteEditMode(null);
    };

    return (
        <MotionBox
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { delay: 0.16, duration: 0 } }}
            display={'flex'}
            onMouseDown={handleMouseDown}
            gap="4px"
            pos="relative"
            alignItems={'center'}
        >
            <NoteTodoToggle isTodo={isTodo} item={item} />
            <ColorButton item={item} />
            <EmojiPickerButton />
            <RemindersButton item={item} />
            <CopyContentButton />
            <Tooltip
                label={isTodo ? 'Detele to-do' : 'Delete note'}
                zIndex="500000"
                placement="top"
                rounded="6px"
                motionProps={tooltipAnimation}
            >
                <Button {...btnStyles} onClick={deleteNote}>
                    <TrashDockIcon size={'20px'} />
                </Button>
            </Tooltip>
        </MotionBox>
    );
};

export default NoteToolbar;

const switchBtn = {
    //bg: 'transparent',
    h: '32px',
    w: '40px',
    p: '0',
    alignItems: 'center',
    size: 'sm',
    my: 'auto',
    rounded: '11px',
    zIndex: 2
};

const NoteTodoToggle = ({ isTodo, item }) => {
    const [localTodo, setLocalTodo] = useState(isTodo);

    //ALL THIS FOR TODO UPDATES FROM WITHIN NOTE
    const updateDockFromOutside = layoutStore(
        (state) => state.updateDockFromOutside
    );
    const initialRender = useRef(true);
    useEffect(() => {
        if (initialRender.current) {
            initialRender.current = false;
            return;
        }
        if (updateDockFromOutside && updateDockFromOutside.type === 'todo') {
            setLocalTodo(updateDockFromOutside.value);
        } else return;
    }, [updateDockFromOutside]);
    //END OF COMMENT COLOR OUTSIDE UPDATES

    const setTodo = (status) => {
        if (status === localTodo) {
            return;
        }
        setLocalTodo(status);
        let updatedItem = { ...item, todo: status };
        const groupId = findGroupId(item.id);
        useStore.getState().updateTab(updatedItem, item.id, groupId);
    };

    const selectedBg = useColorModeValue(
        'linear-gradient(145deg, #5DABFE 0%, #3391FF 100%)',
        'linear-gradient(178deg, #52595D 0%, #40484F 100%)'
    );
    const selectedShadow = useColorModeValue(
        'rgba(70, 94, 193, 0.25) -1px -1px 1px 0px inset',
        'rgb(36 37 42 / 25%) -1px -1px 1px 0px inset'
    );

    const hoverBlockBg = useColorModeValue('#454c5910', '#454c5925');

    return (
        <Flex
            p="2px"
            rounded="14px"
            bg={'categoriesBg'}
            alignItems={'center'}
            gap="4px"
            border={'var(--chakra-colors-teBorder)'}
            boxShadow={'inset 0 1px 4px 0 #454c5914'}
            overflow={'hidden'}
            /* pos="relative" */
        >
            <MotionBox
                bg={selectedBg}
                boxShadow={selectedShadow}
                position={'absolute'}
                animate={{
                    x: localTodo ? 44 : 0,
                    transition: {
                        type: 'spring',
                        stiffness: 700,
                        damping: 40
                    }
                }}
                left={'3px'}
                top={'3px'}
                h="32px"
                w="40px"
                rounded="11px"
                zIndex={1}
                whileHover={{
                    filter: 'brightness(1.05)',
                    background: selectedBg
                }}
            />
            <Tooltip
                label="Set as note"
                zIndex="500000"
                placement="top"
                rounded="6px"
                motionProps={tooltipAnimation}
                gutter={10}
            >
                <Button
                    {...switchBtn}
                    bg={'transparent'}
                    _hover={{
                        filter: 'brightness(1.05)',
                        bg: !localTodo ? 'none' : hoverBlockBg
                    }}
                    onClick={() => setTodo(false)}
                    _active={{ transform: 'scale(0.92)', opacity: 0.8 }}
                >
                    <NoteIcon chosen={!localTodo} />
                </Button>
            </Tooltip>
            <Tooltip
                label="Set as to-do"
                zIndex="500000"
                placement="top"
                rounded="6px"
                motionProps={tooltipAnimation}
                gutter={10}
            >
                <Button
                    {...switchBtn}
                    bg={'transparent'}
                    onClick={() => setTodo(true)}
                    _hover={{
                        filter: 'brightness(1.05)',
                        bg: localTodo ? 'none' : hoverBlockBg
                    }}
                    _active={{
                        transform: 'scale(0.92)',
                        opacity: 0.8
                    }}
                >
                    <TodoIcon chosen={localTodo} />
                </Button>
            </Tooltip>
        </Flex>
    );
};

const ColorButton = ({ item }) => {
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });

    const close = (e) => {
        e.stopPropagation();
        e.preventDefault();
        setShowColorPicker(false);
    };
    const toggle = () => {
        const rect = btnRef.current.getBoundingClientRect();
        setButtonPosition({ top: rect.top, left: rect.left });
        setShowColorPicker(!showColorPicker);
    };

    const [localColor, setColor] = useState(item.commentColor ?? false);

    //ALL THIS FOR COMMENT COLOR UPDATES FROM WITHIN NOTE
    const updateDockFromOutside = layoutStore(
        (state) => state.updateDockFromOutside
    );
    const initialRender = useRef(true);
    useEffect(() => {
        if (initialRender.current) {
            initialRender.current = false;
            return;
        }
        if (updateDockFromOutside && updateDockFromOutside.type === 'color') {
            setColor(updateDockFromOutside.value);
        } else return;
    }, [updateDockFromOutside]);
    //END OF COMMENT COLOR OUTSIDE UPDATES

    const colorPicked = (i) => {
        setShowColorPicker(false);
        let newColor = i === 11 ? false : i;
        setColor(newColor);
        const groupId = findGroupId(item.id);
        let updatedItem = { ...item, commentColor: newColor };
        useStore.getState().updateTab(updatedItem, item.id, groupId);
    };

    const btnRef = useRef(null);

    const cardShadows = useColorModeValue(
        'drop-shadow(0 4px 6px rgba(166,166,167,0.42))',
        'drop-shadow(0 4px 6px rgba(19,20,24,0.8))'
    );
    const lightBg = useColorModeValue('gray.200', '#31363a');

    const isColorDefined =
        item.commentColor !== null &&
        item.commentColor !== undefined &&
        item.commentColor < commentBg.length;
    const bgColor = isColorDefined
        ? commentBg[localColor]?.bg + '96'
        : 'transparent';
    const hoverColor = isColorDefined ? commentBg[localColor]?.bg : lightBg;
    const iconColor = isColorDefined ? commentBg[localColor]?.text : null;

    return (
        <>
            <Tooltip
                label="Set color"
                zIndex="500000"
                placement="top"
                rounded="6px"
                motionProps={tooltipAnimation}
            >
                <Button
                    {...btnStyles}
                    onClick={toggle}
                    ref={btnRef}
                    ml="2px"
                    bg={bgColor}
                    _hover={{
                        bg: hoverColor
                    }}
                >
                    <ColorIcon color={iconColor} />
                </Button>
            </Tooltip>
            <AnimatePresence>
                {showColorPicker && (
                    <Portal>
                        <div
                            onClick={(e) => close(e)}
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                height: '100vh',
                                width: '100vw',
                                zIndex: 999999990,
                                backgroundColor: 'rgba(0,0,0,0.12)',
                                backdropFilter: 'blur(2px)',
                                cursor: 'pointer'
                            }}
                        />
                        <MotionBox
                            initial={{
                                y: 0,
                                rotate: -6
                            }}
                            animate={{
                                y: -12,
                                rotate: -6,
                                transition: { duration: 0.4, ease: 'easeOut' }
                            }}
                            zIndex={999999991}
                            pos="absolute"
                            top={`${buttonPosition.top - 28}px`}
                            left={`${buttonPosition.left}px`}
                            //transform={'rotate(-6deg)'}
                        >
                            {commentBg.map((c, i) => {
                                let radius = 50;
                                const angle =
                                    (i / (commentBg.length - 1)) *
                                    (Math.PI / 3); // Adjust the divisor to change the arc tightness
                                const x = Math.cos(angle) * radius; // Horizontal displacement
                                const y = Math.sin(angle) * radius; // Vertical displacement
                                const isChosen =
                                    localColor === i ||
                                    (localColor === 0 && i === 0);

                                return (
                                    <MotionBox
                                        onClick={() => colorPicked(i)}
                                        pos="absolute"
                                        top={0}
                                        left={0}
                                        initial={{
                                            opacity: 0,
                                            y: 0,
                                            rotate: -45,
                                            x: 0
                                        }}
                                        animate={{
                                            scale: isChosen ? 1.175 : 1,
                                            opacity: 1,
                                            y: i * -32,
                                            rotate: -18 - i * 1,
                                            x:
                                                i * -4 +
                                                Math.sin(
                                                    (i /
                                                        (commentBg.length -
                                                            1)) *
                                                        Math.PI
                                                ) *
                                                    18,
                                            transition: { delay: i * 0.01 }
                                        }}
                                        whileHover={{
                                            scale: 1.225,
                                            zIndex: 26
                                            //background: c.iconLight
                                        }}
                                        whileTap={{
                                            scale: 0.98,
                                            transition: {
                                                duration: 0.2,
                                                delay: 0
                                            }
                                        }}
                                        zIndex={25}
                                        //exit={{ opacity: 1, y: 0, rotate: -45 }}
                                        border={'var(--chakra-colors-teBorder)'}
                                        rounded="12px"
                                        background="groupBg"
                                        p="6px"
                                        alignItems={'center'}
                                        filter={cardShadows}
                                        cursor={'pointer'}
                                        key={i}
                                        role="group"
                                    >
                                        <Box
                                            h="20px"
                                            w="28px"
                                            m="auto"
                                            bg={c.iconLight}
                                            transition={'0.16s'}
                                            rounded="6px"
                                            _groupHover={{ bg: c.bg }}
                                            boxShadow={
                                                isChosen ||
                                                (!isColorDefined && i === 11)
                                                    ? '0 0 0 2px var(--chakra-colors-groupBg), 0 0 0 4px ' +
                                                      c.iconLight
                                                    : 'none'
                                            }
                                        />
                                    </MotionBox>
                                );
                            })}
                        </MotionBox>
                    </Portal>
                )}
            </AnimatePresence>
        </>
    );
};

const EmojiPickerButton = () => {
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });
    const { colorMode } = useColorMode();

    const emojiPickerShadow = useColorModeValue(
        '0 12px 36px -8px rgba(48,49,53,0.36)',
        '0 4px 18px -2px rgba(0,0,0,0.80)'
    );
    const emojiCategory = useColorModeValue('gray.200', '#FFF');

    const close = () => {
        setShowEmojiPicker(false);
    };
    const toggle = () => {
        const rect = btnRef.current.getBoundingClientRect();
        setButtonPosition({ top: rect.top, left: rect.left });
        setShowEmojiPicker(!showEmojiPicker);
    };

    const btnRef = useRef(null);

    const onEmojiClick = (emoji) => {
        setShowEmojiPicker(false);
        const updateInputValue = layoutStore.getState().updateInputValue;
        if (updateInputValue && emoji.native) {
            updateInputValue(emoji.native);
        }
    };

    return (
        <>
            <Tooltip
                label="Add emoji"
                zIndex="500000"
                placement="top"
                rounded="6px"
                motionProps={tooltipAnimation}
            >
                <Button {...btnStyles} onClick={toggle} ref={btnRef}>
                    <SmileyIcon />
                </Button>
            </Tooltip>
            {showEmojiPicker && (
                <Portal>
                    <div
                        onClick={close}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            height: '100vh',
                            width: '100vw',
                            zIndex: 20,
                            backgroundColor: 'rgba(0,0,0,0.12)',
                            backdropFilter: 'blur(2px)',
                            cursor: 'pointer'
                        }}
                    />
                    <MotionBox
                        initial={{
                            y: 0
                        }}
                        animate={{
                            y: -16,
                            transition: { duration: 0.24, ease: 'easeOut' }
                        }}
                        zIndex={999999991}
                        pos="absolute"
                        top={`${buttonPosition.top - 432}px`}
                        left={`${buttonPosition.left - 165}px`}
                        rounded="12px"
                        borderWidth="0px"
                        _focus={{ outline: 'none' }}
                        boxShadow={emojiPickerShadow}
                        border="var(--chakra-colors-teBorder)"
                        w="354px"
                        overflow="hidden"
                    >
                        <Picker
                            data={data}
                            emojiVersion={1}
                            color={emojiCategory}
                            previewPosition={'none'}
                            title="Select a emoji"
                            emoji=""
                            native={true}
                            showPreview={false}
                            onEmojiSelect={onEmojiClick}
                            //onClickOutside={close} <- CAUSES PICKER NOT TO SHOW
                            theme={colorMode === 'light' ? 'light' : 'dark'}
                            searchPosition={'none'}
                        />
                    </MotionBox>
                </Portal>
            )}
        </>
    );
};

const RemindersButton = ({ item }) => {
    const iconDarkerColor = useColorModeValue('#4A5568', '#B0B7C1');
    const emojiPickerShadow = useColorModeValue(
        '0 4px 18px -2px rgba(152,169,185,0.80)',
        '0 4px 18px -2px rgba(0,0,0,0.80)'
    );
    const pickerBg = useColorModeValue('rgba(244, 245, 247, 1)', '#212528');

    const [showRem, setRem] = useState(false);

    const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });

    const toggle = () => {
        const rect = btnRef.current.getBoundingClientRect();
        setButtonPosition({ top: rect.top, left: rect.left });
        setRem(!showRem);
    };

    const btnRef = useRef(null);

    const userData = useStore.getState().userData;
    let userId = userData.userId;

    const hasAlarmWithId =
        item.hasAlarm && item.hasAlarm?.includes(userId) ? true : false;
    const [localHasAlarm, setLocalHasAlarm] = useState(hasAlarmWithId);

    const close = () => {
        setRem(false);
    };
    const closeAndSetRem = () => {
        setLocalHasAlarm(true);
        setRem(false);
    };

    return (
        <>
            <Tooltip
                label={localHasAlarm ? 'Edit reminder' : 'Set reminder'}
                zIndex="500000"
                placement="top"
                rounded="6px"
                motionProps={tooltipAnimation}
            >
                <Button {...btnStyles} onClick={toggle} ref={btnRef}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        style={{
                            width: '20px',
                            height: '20px'
                        }}
                    >
                        <path
                            fill={localHasAlarm ? '#3793ff' : iconDarkerColor}
                            d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z"
                        />
                    </svg>
                </Button>
            </Tooltip>
            {showRem && (
                <Portal>
                    <div
                        onClick={close}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            height: '100vh',
                            width: '100vw',
                            zIndex: 20,
                            backgroundColor: 'rgba(0,0,0,0.12)',
                            //backdropFilter: 'blur(2px)',
                            cursor: 'pointer'
                        }}
                    />
                    <MotionBox
                        initial={{
                            y: 0
                        }}
                        animate={{
                            y: -16,
                            transition: { duration: 0.24, ease: 'easeOut' }
                        }}
                        zIndex={999999991}
                        pos="absolute"
                        top={`${buttonPosition.top - 324}px`}
                        left={`${buttonPosition.left - 124}px`}
                        rounded="12px"
                        borderWidth="0px"
                        _focus={{ outline: 'none' }}
                        boxShadow={emojiPickerShadow}
                        border="var(--chakra-colors-teBorder)"
                        w="354px"
                        overflow="hidden"
                        bg={pickerBg}
                        p="12px 0"
                    >
                        <ReminderPanelMiddle
                            item={item}
                            hasAlarmWithId={localHasAlarm}
                            close={close}
                            closeAndSetRem={closeAndSetRem}
                        />
                    </MotionBox>
                </Portal>
            )}
        </>
    );
};

const ReminderPanelMiddle = ({
    item,
    hasAlarmWithId,
    close,
    closeAndSetRem
}) => {
    const currentWSid = useStore.getState().currentWsId;
    const spaceId = findGroupId(item.id);

    const content = {
        comment: item.comment ?? null,
        commentColor: item.commentColor ?? false,
        id: item.id ?? null,
        type: item.type ?? null,
        todo: item.todo ?? null,
        doneStatus: item.doneStatus ?? null,
        WSid: currentWSid ?? null,
        spaceId: spaceId ?? null
    };

    return (
        <ReminderPanel
            close={close}
            content={content}
            callBackAfterSet={closeAndSetRem}
            hasAlarmWithId={hasAlarmWithId}
        />
    );
};

const CopyContentButton = () => {
    const showToast = useCustomToast();

    const copyContent = () => {
        const noteId = 'note-' + layoutStore.getState().noteEditMode;
        const note = document.getElementById(noteId) as HTMLTextAreaElement;
        if (note) {
            const noteText = note.value;
            handleCopy(noteText)();
        }
    };

    const [copiedText, copy] = useCopyToClipboard();

    const handleCopy = (text: string) => () => {
        copy(text)
            .then(() => {
                //console.log('Copied!', { text });
                showToast({
                    title: 'Text copied',
                    subtitle: 'The note has been copied to your clipboard',
                    type: 'success'
                });
            })
            .catch((error) => {
                showToast({
                    title: 'Something went wrong',
                    subtitle:
                        'Could not copt to your clipboard. Please try again.',
                    type: 'warning'
                });
            });
    };

    return (
        <Tooltip
            label="Copy content"
            zIndex="500000"
            placement="top"
            rounded="6px"
            motionProps={tooltipAnimation}
        >
            <Button {...btnStyles} onClick={copyContent}>
                <CopyIcon size={'20px'} />
            </Button>
        </Tooltip>
    );
};

const iconSize = '20px';

const NoteIcon = ({ chosen }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill={chosen ? '#FFF' : 'var(--chakra-colors-iconMd)'}
            style={{
                width: iconSize,
                height: iconSize
            }}
        >
            <path
                fillRule="evenodd"
                d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z"
                clipRule="evenodd"
            />
        </svg>
    );
};

const TodoIcon = ({ chosen }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill={chosen ? '#FFF' : 'var(--chakra-colors-iconMd)'}
            style={{
                width: iconSize,
                height: iconSize
            }}
        >
            <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
            />
        </svg>
    );
};

const ColorIcon = ({ color }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill={color ? color : 'var(--chakra-colors-iconMd)'}
            style={{
                width: iconSize,
                height: iconSize
            }}
        >
            <path
                fillRule="evenodd"
                d="M4 2a2 2 0 00-2 2v11a3 3 0 106 0V4a2 2 0 00-2-2H4zm1 14a1 1 0 100-2 1 1 0 000 2zm5-1.757l4.9-4.9a2 2 0 000-2.828L13.485 5.1a2 2 0 00-2.828 0L10 5.757v8.486zM16 18H9.071l6-6H16a2 2 0 012 2v2a2 2 0 01-2 2z"
                clipRule="evenodd"
            />
        </svg>
    );
};

const SmileyIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            style={{
                width: '18px',
                height: '18px'
            }}
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 1C5.92487 1 1 5.92487 1 12C1 18.0751 5.92487 23 12 23C18.0751 23 23 18.0751 23 12C23 5.92487 18.0751 1 12 1ZM7.5 9C7.5 8.17157 8.17157 7.5 9 7.5C9.82843 7.5 10.5 8.17157 10.5 9C10.5 9.82843 9.82843 10.5 9 10.5C8.17157 10.5 7.5 9.82843 7.5 9ZM13.5 9C13.5 8.17157 14.1716 7.5 15 7.5C15.8284 7.5 16.5 8.17157 16.5 9C16.5 9.82843 15.8284 10.5 15 10.5C14.1716 10.5 13.5 9.82843 13.5 9ZM7.40006 13.2001C7.84049 12.8697 8.46474 12.9577 8.79692 13.3959L8.80156 13.4018C8.80751 13.4093 8.81883 13.4234 8.83537 13.4431C8.86852 13.4826 8.92225 13.5442 8.99548 13.6207C9.14278 13.7744 9.36413 13.9829 9.65073 14.1913C10.2278 14.611 11.0256 15.0001 12.0001 15.0001C12.9745 15.0001 13.7723 14.611 14.3494 14.1913C14.636 13.9829 14.8573 13.7744 15.0046 13.6207C15.0779 13.5442 15.1316 13.4826 15.1647 13.4431C15.1813 13.4234 15.1926 13.4093 15.1986 13.4018L15.2032 13.3959C15.5354 12.9577 16.1596 12.8697 16.6001 13.2001C17.0419 13.5314 17.1314 14.1582 16.8001 14.6001L16.6967 14.7289C16.6381 14.7987 16.5551 14.8934 16.4486 15.0045C16.2365 15.2258 15.9266 15.5172 15.5257 15.8088C14.7278 16.3891 13.5256 17.0001 12.0001 17.0001C10.4745 17.0001 9.27233 16.3891 8.47439 15.8088C8.07349 15.5172 7.76358 15.2258 7.55151 15.0045C7.44506 14.8934 7.36207 14.7987 7.30342 14.7289C7.26801 14.6867 7.23377 14.6436 7.20006 14.6001C6.86869 14.1582 6.95823 13.5314 7.40006 13.2001Z"
                fill={'var(--chakra-colors-iconMd)'}
            ></path>
        </svg>
    );
};
